(function () {
  const SELECTORS = {
    host: '.rs-actions-host',
    container: '.rs-actions',
    toggle: '.rs-actions__toggle',
    content: '.rs-actions__content',
    activeClass: 'rs-active',
  };
  const SCROLL_CLOSE_DELAY_MS = 100;
  const MENU_VIEWPORT_GUTTER_PX = 8;
  let scrollCloseTimeout;

  // ---------- Helpers ----------
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

  /**
   * @param {HTMLElement} container
   * @returns {HTMLElement | null}
   */
  function findMenuPanel(container) {
    if (!(container instanceof HTMLElement)) return null;
    const stored = /** @type {HTMLElement | undefined} */ (
      /** @type {Record<string, unknown>} */ (container)._rsActionsMenuEl
    );
    if (stored instanceof HTMLElement) return stored;
    const inContainer = $(SELECTORS.content, container);
    if (inContainer instanceof HTMLElement) return inContainer;
    const host = container.closest(SELECTORS.host);
    if (host instanceof HTMLElement) {
      const onHost = host.querySelector(`:scope > ${SELECTORS.content}`);
      if (onHost instanceof HTMLElement) return onHost;
    }
    return null;
  }

  /**
   * @param {HTMLElement} container
   * @returns {HTMLElement | null}
   */
  function getMenuPanel(container) {
    if (!(container instanceof HTMLElement)) return null;
    const menu = findMenuPanel(container);
    if (menu instanceof HTMLElement) {
      /** @type {Record<string, unknown>} */ (container)._rsActionsMenuEl = menu;
    }
    return menu;
  }

  /** @param {HTMLElement} toggle */
  function getContent(toggle) {
    if (!toggle) return null;
    const container = toggle.closest(SELECTORS.container);
    if (!container) return null;

    const id = toggle.getAttribute('aria-controls');
    if (id) {
      const byId = document.getElementById(id);
      if (byId) return byId;
    }
    return getMenuPanel(container);
  }

  /** @param {HTMLElement} container */
  function getToggle(container) {
    return $(SELECTORS.toggle, container);
  }

  /** @param {HTMLElement} container */
  function getMenuMount(container) {
    return container.closest(SELECTORS.host) || container;
  }

  /** @param {HTMLElement} content */
  function resetMenuPosition(content) {
    content.style.position = '';
    content.style.top = '';
    content.style.right = '';
    content.style.bottom = '';
    content.style.left = '';
    content.style.maxHeight = '';
    content.style.overflowY = '';
  }

  /**
   * Keep action menus out of sticky page controls that occupy viewport space.
   * @param {HTMLElement} container
   */
  function positionMenu(container) {
    const content = getMenuPanel(container);
    const toggle = getToggle(container);
    if (!(content instanceof HTMLElement) || !(toggle instanceof HTMLElement)) return;

    resetMenuPosition(content);

    const viewportWidth = window.visualViewport?.width || window.innerWidth;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const toggleRect = toggle.getBoundingClientRect();
    let usableTop = 0;
    let usableBottom = viewportHeight;
    let hasViewportBoundary = false;

    const quickOrderTable = container.closest('.quick-order-table');
    const stickyFooter = quickOrderTable?.querySelector('.rs-table-footer');

    if (
      window.matchMedia('(max-width: 749px)').matches &&
      stickyFooter instanceof HTMLElement &&
      getComputedStyle(stickyFooter).display !== 'none'
    ) {
      const footerRect = stickyFooter.getBoundingClientRect();
      const footerIsVisible = footerRect.width > 0 && footerRect.height > 0 && footerRect.top < viewportHeight && footerRect.bottom > 0;
      if (footerIsVisible) {
        usableBottom = Math.min(usableBottom, footerRect.top);
        hasViewportBoundary = true;
      }
    }

    const cartPage = container.closest('.cart-page');
    const stickyCartTitle = cartPage?.querySelector('.cart-page__title');
    if (stickyCartTitle instanceof HTMLElement && getComputedStyle(stickyCartTitle).display !== 'none') {
      const titleRect = stickyCartTitle.getBoundingClientRect();
      const titleIsVisible = titleRect.width > 0 && titleRect.height > 0 && titleRect.top < viewportHeight && titleRect.bottom > 0;
      if (titleIsVisible) {
        usableTop = Math.max(usableTop, titleRect.bottom);
        hasViewportBoundary = true;
      }
    }

    if (!hasViewportBoundary) return;

    const menuWidth = content.offsetWidth;
    const menuHeight = content.offsetHeight;
    const availableBelow = usableBottom - toggleRect.bottom - MENU_VIEWPORT_GUTTER_PX;
    const availableAbove = toggleRect.top - usableTop - MENU_VIEWPORT_GUTTER_PX;
    const openBelow = menuHeight <= availableBelow || availableBelow >= availableAbove;

    let top = openBelow
      ? toggleRect.bottom + MENU_VIEWPORT_GUTTER_PX
      : toggleRect.top - menuHeight - MENU_VIEWPORT_GUTTER_PX;
    const minTop = usableTop + MENU_VIEWPORT_GUTTER_PX;
    const maxTop = Math.max(minTop, usableBottom - menuHeight - MENU_VIEWPORT_GUTTER_PX);
    top = Math.max(minTop, Math.min(maxTop, top));

    let left = toggleRect.right - menuWidth;
    left = Math.max(
      MENU_VIEWPORT_GUTTER_PX,
      Math.min(viewportWidth - menuWidth - MENU_VIEWPORT_GUTTER_PX, left),
    );

    content.style.position = 'fixed';
    content.style.top = `${top}px`;
    content.style.right = 'auto';
    content.style.bottom = 'auto';
    content.style.left = `${left}px`;
    content.style.maxHeight = `${Math.max(0, usableBottom - usableTop - MENU_VIEWPORT_GUTTER_PX * 2)}px`;
    content.style.overflowY = 'auto';
  }

  /**
   * No menu/group role on the panel — VoiceOver reads "group" for role="menu".
   * @param {HTMLElement} content
   */
  function setMenuPanelA11y(content) {
    if (!(content instanceof HTMLElement)) return;
    content.removeAttribute('role');
    content.removeAttribute('aria-label');
  }

  /**
   * Remove the menu panel from the tree so VoiceOver does not group it with the toggle.
   * @param {HTMLElement} container
   */
  function detachMenuPanel(container) {
    const menu = getMenuPanel(container);
    if (!(menu instanceof HTMLElement)) return;
    setMenuPanelA11y(menu);
    menu.hidden = true;
    menu.setAttribute('aria-hidden', 'true');
    menu.setAttribute('inert', '');
    if (menu.parentElement) {
      menu.parentElement.removeChild(menu);
    }
    /** @type {Record<string, unknown>} */ (container)._rsActionsMenuEl = menu;
  }

  /**
   * Mount the menu on the host (sibling of `.rs-actions`), not inside it with the toggle.
   * @param {HTMLElement} container
   */
  function attachMenuPanel(container) {
    const menu = getMenuPanel(container);
    if (!(menu instanceof HTMLElement)) return;
    const mount = getMenuMount(container);
    if (menu.parentElement !== mount) {
      mount.appendChild(menu);
    }
  }

  /** @param {HTMLElement} container */
  function normalize(container) {
    const toggle = getToggle(container);
    const content = getMenuPanel(container);
    if (toggle) {
      toggle.removeAttribute('aria-haspopup');
      toggle.removeAttribute('aria-expanded');
      toggle.removeAttribute('aria-controls');
      toggle.removeAttribute('aria-labelledby');
      toggle.querySelectorAll('svg').forEach((svg) => {
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
      });
      setMenuButtonAriaLabel(toggle, false);
    }
    if (content) {
      setMenuPanelA11y(content);
      resetMenuPosition(content);
    }
    if (content) {
      if (content.hidden !== true) content.hidden = true;
      content.setAttribute('aria-hidden', 'true');
      content.setAttribute('inert', '');
    }
    container.classList.remove(SELECTORS.activeClass);
    container.closest(SELECTORS.host)?.classList.remove(SELECTORS.activeClass);
  }

  /**
   * @param {HTMLButtonElement} toggle
   * @param {boolean} expanded
   */
  function setMenuButtonAriaLabel(toggle, expanded) {
    if (typeof window.applyMoreActionsMenuAccessibleName === 'function') {
      window.applyMoreActionsMenuAccessibleName(toggle, expanded);
      return;
    }
    const text =
      expanded && toggle.dataset.ariaLabelExpanded
        ? toggle.dataset.ariaLabelExpanded
        : toggle.dataset.ariaLabelCollapsed;
    if (text) toggle.setAttribute('aria-label', text);
  }

  /** @param {HTMLElement} container */
  function open(container) {
    const toggle = getToggle(container);
    const content = getMenuPanel(container);
    if (!toggle || !content) return;
    clearTimeout(scrollCloseTimeout);
    closeAll(container);
    attachMenuPanel(container);
    setMenuButtonAriaLabel(toggle, true);
    setMenuPanelA11y(content);
    content.hidden = false;
    content.removeAttribute('aria-hidden');
    content.removeAttribute('inert');
    if (typeof window.syncMoreActionsMenuItemLabels === 'function') {
      window.syncMoreActionsMenuItemLabels(content);
    }
    container.classList.add(SELECTORS.activeClass);
    container.closest(SELECTORS.host)?.classList.add(SELECTORS.activeClass);
    positionMenu(container);
  }

  /** @param {HTMLElement} container */
  function close(container) {
    if (!(container instanceof HTMLElement)) return;
    const toggle = getToggle(container);
    if (toggle) {
      setMenuButtonAriaLabel(toggle, false);
      toggle.classList.remove('is-open');
    }
    const content = getMenuPanel(container);
    if (content) resetMenuPosition(content);
    detachMenuPanel(container);
    container.classList.remove(SELECTORS.activeClass);
    container.closest(SELECTORS.host)?.classList.remove(SELECTORS.activeClass);
  }

  /** Close all except optional container */
  function closeAll(except = null) {
    $$(SELECTORS.container).forEach((c) => {
      if (c !== except && c.classList.contains(SELECTORS.activeClass)) close(c);
    });
  }

  /**
   * After cloneNode the menu may live on the host, only in `_rsActionsMenuEl`, or be missing entirely.
   * Remount a closed panel inside `.rs-actions` so open() can find it again.
   * @param {HTMLElement} container
   * @param {HTMLElement} [menuTemplate] fallback `.rs-actions__content` to clone when missing
   * @returns {boolean}
   */
  function resetClonedContainer(container, menuTemplate) {
    if (!(container instanceof HTMLElement)) return false;

    let menu = findMenuPanel(container);
    if (!(menu instanceof HTMLElement) && menuTemplate instanceof HTMLElement) {
      menu = /** @type {HTMLElement} */ (menuTemplate.cloneNode(true));
    }
    if (!(menu instanceof HTMLElement)) return false;

    /** @type {Record<string, unknown>} */ (container)._rsActionsMenuEl = menu;

    const host = container.closest(SELECTORS.host);
    if (host instanceof HTMLElement) {
      host.classList.remove(SELECTORS.activeClass);
      host.querySelectorAll(`:scope > ${SELECTORS.content}`).forEach((panel) => {
        if (panel !== menu) panel.remove();
      });
    }

    if (!container.contains(menu)) {
      container.appendChild(menu);
    }

    normalize(container);
    return true;
  }

  // ---------- Public init ----------
  /**
   * Normalize one, many, or all containers (no event binding needed per node)
   * @param {HTMLElement|string=} target
   */
  function init(target) {
    const containers = target ? (typeof target === 'string' ? $$(target) : [target]) : $$(SELECTORS.container);
    containers.forEach(normalize);
  }

  // ---------- Global (delegated) listeners ----------
  document.addEventListener('click', (e) => {
    const t = /** @type {HTMLElement} */ (e.target);

    // 1) Toggle clicked
    const toggle = t?.closest?.(SELECTORS.toggle);
    if (toggle) {
      e.preventDefault();
      const container = toggle.closest(SELECTORS.container);
      if (!container) return;
      if (container.getAttribute('data-rsa-apg-wired') === '1') return;

      if (!toggle.getAttribute('aria-label')) normalize(container);

      const isOpen = container.classList.contains(SELECTORS.activeClass);
      if (isOpen) {
        close(container);
      } else {
        open(container);
      }
      return;
    }

    // 2) Click outside any rs-actions => close all
    if (!t?.closest?.(SELECTORS.container)) {
      // Don't close when interacting with a modal opened from an rs-actions menu
      if (t?.closest?.('delivery-dates-modal')) return;
      // When a host is used, attachMenuPanel moves .rs-actions__content to .rs-actions-host
      // as a sibling of .rs-actions. Clicking an action item there is no longer "inside"
      // .rs-actions, so we defer closeAll to let same-event handlers (e.g. rs-private-label)
      // traverse the DOM before the content is detached.
      if (t?.closest?.(SELECTORS.host)) {
        setTimeout(() => closeAll(), 0);
      } else {
        closeAll();
      }
    }
  });

  // Keyboard support (Enter/Space on focused toggle, ESC anywhere)
  document.addEventListener('keydown', (e) => {
    const active = /** @type {HTMLElement} */ (document.activeElement);
    if ((e.key === 'Enter' || e.key === ' ') && active?.matches?.(SELECTORS.toggle)) {
      e.preventDefault();
      const container = active.closest(SELECTORS.container);
      if (!container) return;
      if (container.getAttribute('data-rsa-apg-wired') === '1') return;

      if (!active.getAttribute('aria-label')) normalize(container);

      const isOpen = container.classList.contains(SELECTORS.activeClass);
      isOpen ? close(container) : open(container);
    }
    if (e.key === 'Escape') {
      closeAll();
    }
  });

  document.addEventListener(
    'scroll',
    () => {
      const openContainers = $$(SELECTORS.container).filter(
        (container) =>
          container.classList.contains(SELECTORS.activeClass) &&
          container.getAttribute('data-rsa-apg-wired') !== '1',
      );
      if (openContainers.length === 0) return;

      openContainers.forEach(positionMenu);

      clearTimeout(scrollCloseTimeout);
      scrollCloseTimeout = setTimeout(() => {
        openContainers.forEach((container) => {
          if (container.classList.contains(SELECTORS.activeClass)) close(container);
        });
      }, SCROLL_CLOSE_DELAY_MS);
    },
    {capture: true, passive: true},
  );

  const repositionOpenMenus = () => {
    $$(SELECTORS.container).forEach((container) => {
      if (container.classList.contains(SELECTORS.activeClass)) positionMenu(container);
    });
  };
  window.addEventListener('resize', repositionOpenMenus, {passive: true});
  window.visualViewport?.addEventListener('resize', repositionOpenMenus, {passive: true});

  // Normalize everything on DOM ready
  document.addEventListener('DOMContentLoaded', () => init());

  // ---------- Expose minimal API ----------
  if (typeof window !== 'undefined') {
    window.RsActions = {
      init,
      open,
      close,
      closeAll,
      attachMenuPanel,
      detachMenuPanel,
      getMenuPanel,
      positionMenu,
      resetClonedContainer,
    };
  }
})();
